import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

import { getBrowserTimezone } from '../utils/timezone';

import { get, post, put, del } from '../utils/api';
const reportingServiceAPIURL = import.meta.env
  .REACT_APP_REPORTING_SERVICE_API_URL;

const UPDATE_ALLOWED_FIELDS = [
  'frequency',
  'executionTime',
  'timezone',
  'propertyIds',
  'targetEmails',
  'fileType',
  'exportAs',
  'dayOfWeek',
  'dayOfMonth',
  'executionDate',
  'startDate',
  'endDate',
  'dateRangeType',
  'propertyGroupId',
  // leaseType is required by the Lease Change Out report. ENG-27547.
  'leaseType',
];

export default class ScheduledReportService {
  /**
   * Create a new scheduled report
   * @param {Object} params - Schedule parameters
   * @param {string} params.organizationId - Organization ID
   * @param {string} params.reportId - Report ID
   * @param {'legacy' | 'new'} params.reportVersion - Report version
   * @param {'daily' | 'weekly' | 'monthly' | 'one-time'} params.frequency - Schedule frequency
   * @param {string} params.executionTime - Execution time in HH:mm format
   * @param {string} [params.timezone] - Timezone (defaults to the browser
   *   timezone, falling back to America/Chicago when Intl is unavailable)
   * @param {string[]} params.propertyIds - Property IDs
   * @param {string[]} params.targetEmails - Target email addresses
   * @param {'createdAt' | 'finishedDate'} [params.dateRangeType] - Date range type for legacy reports
   * @param {string} [params.projectName] - Project name for legacy reports
   * @param {'csv' | 'xlsx' | 'pdf'} [params.fileType] - File type for new reports
   * @param {'grouped' | 'individual'} [params.exportAs] - Export format for new reports
   * @param {number} [params.dayOfWeek] - Day of week for weekly scheduling (0-6)
   * @param {number} [params.dayOfMonth] - Day of month for monthly scheduling (1-31)
   * @param {string} [params.executionDate] - Execution date for one-time scheduling (YYYY-MM-DD)
   * @param {string} [params.startDate] - Start date for recurring schedules (YYYY-MM-DD)
   * @param {string} [params.endDate] - End date for recurring schedules (YYYY-MM-DD)
   * @param {string} [params.propertyGroupId] - Property group ID
   * @param {boolean} [params.isPropertyLevel] - Whether the report is property-level
   * @param {boolean} [params.runByPeriod] - Whether the legacy report queries by fiscal period (YYYYMM)
   * @param {Object} [options] - Request options
   * @returns {Promise} - Created schedule
   */
  createSchedule(params, options) {
    const body = {
      organizationId: params.organizationId,
      reportId: params.reportId,
      reportVersion: params.reportVersion,
      frequency: params.frequency,
      executionTime: params.executionTime,
      propertyIds: params.propertyIds,
      targetEmails: params.targetEmails,
    };

    // Default timezone to the user's browser timezone so EventBridge Scheduler fires
    // in the timezone the user actually picked the executionTime from. ENG-27666.
    body.timezone = params.timezone || getBrowserTimezone();

    // Add optional fields
    if (params.dateRangeType) {
      body.dateRangeType = params.dateRangeType;
    }
    // leaseType is required by the Lease Change Out report. ENG-27547.
    if (params.leaseType) {
      body.leaseType = params.leaseType;
    }
    if (params.projectName) {
      body.projectName = params.projectName;
    }
    if (params.fileType) {
      body.fileType = params.fileType;
    }
    if (params.exportAs) {
      body.exportAs = params.exportAs;
    }
    if (params.dayOfWeek !== undefined) {
      body.dayOfWeek = params.dayOfWeek;
    }
    if (params.dayOfMonth !== undefined) {
      body.dayOfMonth = params.dayOfMonth;
    }
    if (params.executionDate) {
      body.executionDate = params.executionDate;
    }
    if (params.startDate) {
      body.startDate = params.startDate;
    }
    if (params.endDate) {
      body.endDate = params.endDate;
    }
    if (params.propertyGroupId) {
      body.propertyGroupId = params.propertyGroupId;
    }
    if (params.isPropertyLevel !== undefined) {
      body.isPropertyLevel = params.isPropertyLevel;
    }
    if (params.runByPeriod !== undefined) {
      body.runByPeriod = params.runByPeriod;
    }

    return post(
      `${reportingServiceAPIURL}/report-schedules`,
      JSON.stringify(body),
      {
        ...options,
        isOtherApi: true,
      },
    );
  }

  /**
   * List scheduled reports
   * @param {Object} params - Query parameters
   * @param {string} params.organizationId - Organization ID (required)
   * @param {string} [params.reportId] - Optional report ID filter
   * @param {Object} [options] - Request options
   * @returns {Promise} - List of schedules with totalCount
   */
  listSchedules(params, options) {
    if (!params.organizationId) {
      throw new Error('organizationId is required');
    }

    const queryParams = new URLSearchParams({
      organizationId: params.organizationId,
    });

    if (params.reportId) {
      queryParams.append('reportId', params.reportId);
    }

    return get(
      `${reportingServiceAPIURL}/report-schedules?${queryParams.toString()}`,
      {
        ...options,
        isOtherApi: true,
      },
    );
  }

  /**
   * Get a specific scheduled report by ID
   * @param {string} id - Schedule ID
   * @param {Object} [options] - Request options
   * @returns {Promise} - Schedule details
   */
  getSchedule(id, options) {
    if (!id) {
      throw new Error('Schedule ID is required');
    }

    return get(`${reportingServiceAPIURL}/report-schedules/${id}`, {
      ...options,
      isOtherApi: true,
    });
  }

  /**
   * Delete a scheduled report
   * @param {string} id - Schedule ID
   * @param {Object} [options] - Request options
   * @returns {Promise} - Deletion result with success flag and id
   */
  deleteSchedule(id, options) {
    if (!id) {
      throw new Error('Schedule ID is required');
    }

    return del(`${reportingServiceAPIURL}/report-schedules/${id}`, undefined, {
      ...options,
      isOtherApi: true,
    });
  }

  /**
   * Update an existing scheduled report
   * @param {string} id - Schedule ID
   * @param {Object} params - Schedule update parameters
   * @param {'daily' | 'weekly' | 'monthly' | 'one-time'} [params.frequency] - Schedule frequency
   * @param {string} [params.executionTime] - Execution time in HH:mm format
   * @param {string} [params.timezone] - Timezone
   * @param {string[]} [params.propertyIds] - Property IDs
   * @param {string[]} [params.targetEmails] - Target email addresses
   * @param {'csv' | 'xlsx' | 'pdf'} [params.fileType] - File type for new reports
   * @param {'grouped' | 'individual'} [params.exportAs] - Export format for new reports
   * @param {number} [params.dayOfWeek] - Day of week for weekly scheduling (0-6)
   * @param {number} [params.dayOfMonth] - Day of month for monthly scheduling (1-31)
   * @param {string} [params.executionDate] - Execution date for one-time scheduling (YYYY-MM-DD)
   * @param {string} [params.startDate] - Start date for recurring schedules (YYYY-MM-DD)
   * @param {string} [params.endDate] - End date for recurring schedules (YYYY-MM-DD)
   * @param {'createdAt' | 'finishedDate'} [params.dateRangeType] - Date range type for reports
   * @param {string} [params.propertyGroupId] - Property group ID
   * @param {Object} [options] - Request options
   * @returns {Promise} - Updated schedule
   */
  updateSchedule(id, params, options) {
    if (!id) {
      throw new Error('Schedule ID is required');
    }

    // Use pickBy to filter out undefined values
    // Note: using !== undefined to allow 0 for dayOfWeek/dayOfMonth
    const body = pickBy(
      pick(params, UPDATE_ALLOWED_FIELDS),
      (value) => value !== undefined,
    );

    return put(
      `${reportingServiceAPIURL}/report-schedules/${id}`,
      JSON.stringify(body),
      {
        ...options,
        isOtherApi: true,
      },
    );
  }
}
